<script setup>
import { inject, shallowRef, useTemplateRef, watch, computed, toValue } from 'vue'
import Form from '@provise/components/form.vue'
import Step from './step.vue'
import Uploads from './uploads.vue'
import { v4 as uuid4 } from 'uuid'
import Editor from '@/components/editor/editor.vue'
import Datepicker from '@provise/components/datepicker.vue'
import { commons } from '@provise/lib/transformer'
import stepsConfig from './steps'

defineProps({
  edit: Boolean,
  editPermission: {
    type: Boolean,
    default: true,
  },
})
defineEmits(['save', 'refresh'])
const model = defineModel()
const [replies] = inject('replies')
const stepsNames = Object.keys(stepsConfig)
const s = [['', 'investigation_result', 'Результат расследования']]
const is = {
  date_eqv_finish: [
    null,
    mv => (mv.date_eqv_finish ? commons.dateLocal(mv.date_eqv_finish, 'P') : '–'),
    mv =>
      mv?.request_category?.sysname == 'missed_information'
        ? 'Дата завершения расследования'
        : 'Дата завершения расследования',
  ],
  investigation_result: [null, mv => [mv.investigation_result]],
  need_commercial_approve: [
    null,
    mv => (mv.need_commercial_approve ? 'Да' : 'Нет'),
    'Уведомить коммерческий отдел',
  ],
  no_approval: [null, mv => (mv.no_approval ? 'Да' : 'Нет'), 'Ответ без согласования'],
}
const switchNCA = 'switch-' + uuid4()
const switchNoApprove = 'switch-na-' + uuid4()

const isEditing = shallowRef(false)
const form = useTemplateRef('complaint-investigation-form')
const apiResource = inject('apiResource')

const nCAEnabled = computed(() => {
  const mv = toValue(model)
  const currStatusIdx = stepsNames.indexOf(mv.status?.key)
  const coordStatusIdx = stepsNames.indexOf('coordination')
  return currStatusIdx < coordStatusIdx
})

const noApproveEnabled = computed(() => {
  return !toValue(replies).length
})

watch(
  [() => model.value.no_approve, () => model.value.need_commercial_approve],
  ([noApprove, comApprove], [oldNoApprove, oldComApprove]) => {
    if (noApprove && oldNoApprove != noApprove) {
      model.value = { ...model.value, need_commercial_approve: false }
    }
    if (comApprove && oldComApprove != comApprove) {
      model.value = { ...model.value, no_approve: false }
    }
  },
  { flush: 'post' }
)
</script>
<template>
  <Step v-model="model" :edit="edit" :sch="s" :isch="is" name="investigation">
    <Form
      :entity="model"
      entity-name="complaints"
      :entity-rbac="['common']"
      :edit-permission="editPermission"
      :edit-label="model.sla_users?.investigation ? null : 'Взять в работу'"
      @form:apply="$emit('save', () => form.reset())"
      @form:editable="isEditing = $event"
      @form:reset="apiResource('get')"
      sticky-controls
      ref="complaint-investigation-form"
    >
      <div class="mb-3 d-flex">
        <div>
          <label class="form-label pt-2">{{
            model?.request_category?.sysname == 'missed_information'
              ? 'Дата завершения расследования'
              : 'Дата завершения расследования'
          }}</label>
        </div>
        <div class="ms-5">
          <Datepicker v-model="model.date_eqv_finish" />
        </div>
        <div class="ms-3 pt-2">
          <span v-if="model.date_eqv_finish == null" class="text-danger small"
            >Заполните дату, чтобы перейти к следующему шагу</span
          >
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label" for="request-investigation-result">Результат расследования</label>
        <Editor v-model="model.investigation_result" />
      </div>

      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          :id="switchNCA"
          :disabled="!nCAEnabled"
          v-model="model.need_commercial_approve"
        />
        <label class="form-check-label" :for="switchNCA"> Уведомить коммерческий отдел</label>
      </div>

      <Uploads v-model="model" division="complaint.investigation" />

      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          :id="switchNoApprove"
          :disabled="!noApproveEnabled"
          v-model="model.no_approve"
        />
        <label class="form-check-label" :for="switchNoApprove">
          Перейти к ответу без согласования</label
        >
      </div>

      <div v-if="$slots.sla">
        <slot name="sla" />
      </div>
      <template #post-controls v-if="$slots['form-post-controls'] && !isEditing">
        <slot name="form-post-controls" />
      </template>
    </Form>
  </Step>
</template>
